import React, { useEffect, useState } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { isAdSpendMetricId } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useCreateRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useConvertPaidTrafficFieldName } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/PaidSearch/paidSearchUtil';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { usePaidSearchMetric } from './PaidSearchMetricContext';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const options = [
  {
    id: 'spend',
    label: 'Ad Spend'
  },
  {
    id: 'clicks',
    label: 'Ad Clicks'
  },
  {
    id: 'impressions',
    label: 'Impressions'
  },
  {
    id: 'clickThroughRate',
    label: 'CTR %'
  },
  {
    id: 'costPerClick',
    label: 'CPC'
  },
  {
    id: 'costPerMilli',
    label: 'CPM'
  },
  {
    id: 'costPerAcquisition',
    label: 'CPA'
  },
  {
    id: 'sales',
    label: 'Ad Sales'
  },
  {
    id: 'conversionRate',
    label: 'Conversion Rate'
  },
  {
    id: 'unitsSold',
    label: 'Ad Units Sold'
  },
  {
    id: 'returnOnAdSpend',
    label: 'ROAS'
  }
];
/**
 * Shows ad trend chart with a dropdown
 */
const AdvertisingMultiMetricTrendChart = () => {
  const { selectedMetric } = usePaidSearchMetric();
  const [localFieldName, setLocalFieldName] = useState('spend');

  useEffect(() => {
    if (selectedMetric) {
      setLocalFieldName(selectedMetric);
    }
  }, [selectedMetric]);

  const mainEntityType = useAppSelector((state) => state.entityService.mainEntity.type);
  const { isProductEntity } = useProductEntity();
  const { convertedBuilderFunc } = useConvertPaidTrafficFieldName();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();

  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = () => {
    const displayName = options.find((option) => option.id === localFieldName)?.label ?? '';
    const metricName = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR %'].includes(displayName)
      ? displayName
      : displayName.toLowerCase();
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${metricName} for ${entityPhrase}`}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  const isAdSpendMetric = isAdSpendMetricId(localFieldName);

  return (
    <BeaconComparableSplineChart
      indexName="advertising"
      fieldName={localFieldName}
      beaconChartWithLegendOverride={
        shouldShowBeaconProTopics()
          ? (props) => ({
              ...props,
              includeSummaryInfo: false,
              subtitle: getSubtitle(),
              primaryLegendProps: {
                ...props.primaryLegendProps,
                ...(isAdSpendMetric && { changeColorOverride: 'secondary' as const })
              }
            })
          : (props) => ({
              ...props,
              primaryLegendProps: {
                ...props.primaryLegendProps,
                ...(isAdSpendMetric && { changeColorOverride: 'secondary' as const })
              }
            })
      }
      singleDropdownProps={{
        defaultLabel: '',
        options,
        selectedId: localFieldName,
        onChange: (option) => setLocalFieldName(option.id)
      }}
      requestBuilder={(builder) => convertedBuilderFunc(builder.apply(removeRetailPriceRangeFilters()))}
    />
  );
};

export default AdvertisingMultiMetricTrendChart;
